import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { AlertTriangle, AlertCircle, Info, ExternalLink, CheckCircle } from 'lucide-react';
import type { Alert } from '../../services/dashboardService';

interface DashboardAlertsProps {
  alerts: Alert[];
  isLoading?: boolean;
}

export const DashboardAlerts: React.FC<DashboardAlertsProps> = ({
  alerts,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-warning-500" />
            <span>Alerts & Insights</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse h-16 bg-glass-surface dark:bg-glass-surface-dark-mode rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!alerts || alerts.length === 0) {
    return (
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-success-500" />
            <span>Alerts & Insights</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Info className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
            <p className="text-text-secondary">No alerts at this time. All systems operational!</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high':
        return <AlertTriangle className="w-5 h-5 text-error-500" />;
      case 'medium':
        return <AlertCircle className="w-5 h-5 text-warning-500" />;
      default:
        return <Info className="w-5 h-5 text-primary-500" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'border-error-500/50 bg-error-500/10';
      case 'medium':
        return 'border-warning-500/50 bg-warning-500/10';
      default:
        return 'border-primary-500/50 bg-primary-500/10';
    }
  };

  const handleAlertClick = (alert: Alert) => {
    if (alert.link_url) {
      window.location.href = alert.link_url;
    }
  };

  return (
    <Card className="glass-card hover-lift">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <AlertTriangle className="w-5 h-5 text-warning-500" />
          <span>Alerts & Insights</span>
          <span className="ml-auto px-3 py-1 bg-warning-500/20 text-warning-500 rounded-full text-sm font-semibold">
            {alerts.length}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {alerts.map((alert) => (
            <div
              key={alert.id}
              className={`p-4 rounded-lg border ${getSeverityColor(alert.severity)} transition-all hover:shadow-lg cursor-pointer`}
              onClick={() => handleAlertClick(alert)}
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                  {getSeverityIcon(alert.severity)}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-body-md font-semibold text-text-primary mb-1">
                    {alert.title}
                  </h4>
                  <p className="text-body-sm text-text-secondary mb-2">
                    {alert.description}
                  </p>
                  {alert.actionable && alert.link_url && (
                    <div className="flex items-center space-x-2 text-primary-500 text-body-sm font-medium">
                      <span>View Details</span>
                      <ExternalLink className="w-4 h-4" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default DashboardAlerts;

